import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { projects } from "../mock";
import Header from "../components/Header";
import ProjectDetailEditorial from "./ProjectDetailEditorial";
import { playTick } from "../lib/tick";
import { useEdgeSwipeBack } from "../lib/useEdgeSwipeBack";

// Slugs that use the new editorial layout. All other projects keep the original 2x4 grid layout.
const EDITORIAL_SLUGS = new Set(["500m-divers", "stellar-1", "300m-divers", "squircle", "stellar-2-luna", "stellar-3-micromotor"]);

// A grid tile — renders a real image when provided, otherwise the placeholder
const GridTile = ({ tile, alt, onClick }) => (
  <button
    onClick={onClick}
    className="aspect-square flex flex-col items-center justify-center text-muted-ink relative overflow-hidden group cursor-pointer"
    style={{ backgroundColor: tile.bg || "#EFEDE6" }}
    aria-label={tile.url ? `Open ${alt}` : tile.label}
  >
    {tile.url ? (
      <div
        className="w-full h-full"
        style={{ transform: tile.scale && tile.scale !== 1 ? `scale(${tile.scale})` : undefined }}
      >
        <img
          src={tile.url}
          alt={alt}
          loading="lazy"
          style={{ objectPosition: tile.position || "center" }}
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
        />
      </div>
    ) : (
      <>
        <ImageIcon className="w-7 h-7 opacity-40 group-hover:opacity-70 transition-opacity" strokeWidth={1} />
        <span className="mono text-[10px] uppercase tracking-widest mt-3 opacity-60 group-hover:opacity-90 transition-opacity">
          {tile.label}
        </span>
        <div className="absolute inset-0 grain pointer-events-none" />
      </>
    )}
  </button>
);

// Concise narrative — fallback template if a project doesn't ship its own narrative
const getDefaultSections = (p) => [
  {
    heading: "Strategic Positioning & Architecture",
    body: [`The ${p.title} was developed for ${p.client.replace("\u2014", "—")} as a focused, restrained statement piece. As lead designer, I built it on our proven case architecture to keep the development lean while elevating proportion, finish and dial language to a flagship standard.`]
  },
  {
    heading: "Technical Design & Dial Engineering",
    body: [`I personally executed the technical drawings, dial layouts and applied-element specifications. The case was machined in ${(p.tags[2] || "stainless steel").toLowerCase()} with case-back tolerances tightened to within 0.02mm, and the dial was redrawn at 1:1 scale to ensure 100% fidelity from concept to series production.`]
  },
  {
    heading: "Material & Manufacturing",
    body: [`The strap, packaging and accessory program were developed in parallel with our manufacturing partners. I coordinated supplier selection, material qualification and finishing trials to keep the watch honest in its material story while remaining viable at production volume.`]
  },
  {
    heading: "Outcome & Recognition",
    body: [`The watch shipped on schedule and has been recognised within the industry — extending the collection's commercial life and reinforcing the brand's design language. The project demonstrates the full lifecycle: concept, technical execution, supply chain and storytelling.`]
  }
];

// Normalise body to always be an array of paragraphs
const normaliseSection = (s) => ({
  heading: s.heading,
  body: Array.isArray(s.body) ? s.body : [s.body]
});

const Lightbox = ({ tiles, index, setIndex, onClose }) => {
  const prev = useCallback(() => setIndex((i) => (i - 1 + tiles.length) % tiles.length), [tiles.length, setIndex]);
  const next = useCallback(() => setIndex((i) => (i + 1) % tiles.length), [tiles.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-300"
    >
      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 shadow-lg"
      >
        <X className="w-5 h-5" strokeWidth={1.75} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 mono text-xs uppercase tracking-widest text-paper/60">
        {String(index + 1).padStart(2, "0")} / {String(tiles.length).padStart(2, "0")}
      </div>

      {/* Prev */}
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        aria-label="Previous"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 z-10 shadow-lg"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.75} />
      </button>

      {/* Image (clicks on the image itself do NOT close) */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[80vh] flex items-center justify-center cursor-default"
      >
        {tiles[index].url ? (
          <div
            className="flex items-center justify-center max-w-full max-h-[80vh]"
            style={{ backgroundColor: tiles[index].bg || "transparent" }}
          >
            <img
              src={tiles[index].url}
              alt={tiles[index].label}
              className="max-w-full max-h-[80vh] w-auto h-auto object-contain shadow-2xl"
            />
          </div>
        ) : (
          <div className="w-full aspect-[4/5] sm:aspect-[16/10] bg-[#EFEDE6] flex flex-col items-center justify-center text-muted-ink relative overflow-hidden">
            <ImageIcon className="w-16 h-16 opacity-30" strokeWidth={1} />
            <span className="serif text-2xl md:text-3xl mt-6 text-ink/70">
              {tiles[index].label}
            </span>
            <span className="mono text-[11px] uppercase tracking-widest mt-3 text-muted-ink">
              Image placeholder
            </span>
            <div className="absolute inset-0 grain pointer-events-none" />
          </div>
        )}
      </div>

      {/* Next */}
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        aria-label="Next"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 z-10 shadow-lg"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.75} />
      </button>
    </div>
  );
};

const ProjectDetail = () => {
  useEdgeSwipeBack();
  const { slug } = useParams();
  const nav = useNavigate();
  const idx = projects.findIndex((p) => p.slug === slug);
  const p = projects[idx];
  const next = projects[(idx + 1) % projects.length];
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!p) {
    return (
      <main className="bg-paper text-ink min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <p className="serif text-4xl mb-4">Project not found.</p>
          <Link to="/" className="hover-line text-sm">Back home</Link>
        </div>
      </main>
    );
  }

  // Route to the editorial layout for selected slugs
  if (EDITORIAL_SLUGS.has(p.slug)) {
    const isLooping = idx === projects.length - 1;
    return <ProjectDetailEditorial project={p} next={next} isLooping={isLooping} />;
  }

  const galleryUrls = Array.isArray(p.gallery) ? p.gallery : [];
  const tiles = Array.from({ length: 8 }, (_, i) => {
    const item = galleryUrls[i];
    if (!item) return { url: null, bg: null, scale: 1, position: "center", label: `Detail ${String(i + 1).padStart(2, "0")}` };
    if (typeof item === "string") return { url: item, bg: null, scale: 1, position: "center", label: `Detail ${String(i + 1).padStart(2, "0")}` };
    return { url: item.url, bg: item.bg || null, scale: item.scale || 1, position: item.position || "center", label: item.label || `Detail ${String(i + 1).padStart(2, "0")}` };
  });
  const rawSections = p.narrative && p.narrative.length ? p.narrative : getDefaultSections(p);
  const sections = rawSections.map(normaliseSection);

  return (
    <main className="bg-paper text-ink min-h-screen">
      <Header />

      {/* Project name + 2-line description (BEFORE the grid) */}
      <section className="pt-40 md:pt-56 pb-10 md:pb-14 px-10 md:px-20">
        <div className="max-w-[1700px] mx-auto">
          <button
            onClick={() => { playTick(); nav(-1); }}
            className="inline-flex items-center gap-2 mono text-xs uppercase tracking-widest text-muted-ink hover:text-ink mb-10"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.25} /> Back
          </button>

          <p className="mono text-[11px] uppercase tracking-widest text-muted-ink mb-4">
            {p.client}
          </p>

          <h1 className="sohne tracking-tight leading-[0.98] text-3xl md:text-5xl lg:text-6xl">
            {p.title}
          </h1>

          {/* 2-line brief description */}
          <p className="mt-12 md:mt-16 text-sm md:text-[15px] leading-[1.7] text-ink/85 max-w-2xl font-light">
            {p.summary}
          </p>
        </div>
      </section>

      {/* 2 × 4 grid — full bleed, no gaps, square tiles */}
      <section className="pb-16 md:pb-24">
        <div className="w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {tiles.map((t, i) => (
              <GridTile
                key={i}
                tile={t}
                alt={`${p.title} — ${t.label}`}
                onClick={() => setLightboxIdx(i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 2-column grid write-up — pairs first two sections side-by-side */}
      <section className="pb-20 md:pb-28 px-10 md:px-20">
        <div className="max-w-[1700px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-10 md:gap-y-12">
          {sections.map((s) => (
            <div key={s.heading}>
              <h3 className="text-[15px] md:text-base font-semibold tracking-tight mb-5">
                {s.heading}
              </h3>
              <div className="space-y-4 max-w-[60ch]">
                {s.body.map((para, i) => (
                  <p key={i} className="text-sm md:text-[15px] font-light leading-[1.7] text-ink/85">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Next project */}
      <section className="border-t border-ink py-14 md:py-20 px-10 md:px-20">
        <div className="max-w-[1700px] mx-auto">
          <Link
            to={`/work/${next.slug}`}
            onClick={() => playTick()}
            className="group flex items-center justify-between"
          >
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-muted-ink mb-3">
                Next project
              </div>
              <div className="sohne text-3xl md:text-5xl tracking-tight transition-all duration-500">
                {next.title}
              </div>
            </div>
            <ArrowUpRight
              className="w-9 h-9 md:w-12 md:h-12 transition-transform duration-500 group-hover:rotate-45"
              strokeWidth={1}
            />
          </Link>
        </div>
      </section>

      {lightboxIdx !== null && (
        <Lightbox
          tiles={tiles}
          index={lightboxIdx}
          setIndex={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </main>
  );
};

export default ProjectDetail;
